import React from 'react'
import {Pressable, RefreshControl, ScrollView, View} from 'react-native'
import {Trans} from '@lingui/macro'

import {NEWS_AUTHOR_HANDLE, NEWS_REQUIRED_TAG} from '#/lib/news/config'
import {NEWS_CATEGORIES} from '#/lib/news/newsCategories'
import {
  fetchTellusArticlePreviews,
  groupArticlesByCategory,
} from '#/lib/news/newsData'
import {displayAtproto} from '#/lib/strings/identifiers'
import {useAgent} from '#/state/session'
import {atoms as a, select, useBreakpoints, useTheme, web} from '#/alf'
import * as Grid from '#/components/Grid'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import {NewsCard} from '#/components/news/NewsCard'
import {Text} from '#/components/Typography'
import {type ArticlePreview} from '#/features/news/types'

export default function NewsScreen() {
  const t = useTheme()
  const {gtMobile, gtTablet} = useBreakpoints()
  const agent = useAgent()
  const [activeCategoryId, setActiveCategoryId] = React.useState(
    NEWS_CATEGORIES[0]?.id ?? 'top',
  )
  const [activeTopic, setActiveTopic] = React.useState('IA')
  const [articlesByCategory, setArticlesByCategory] = React.useState<
    Record<string, ArticlePreview[]>
  >({})
  const [isRefreshing, setIsRefreshing] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)

  const topCategoryId = NEWS_CATEGORIES[0]?.id ?? 'top'
  const refreshAll = React.useCallback(async () => {
    setIsRefreshing(true)
    try {
      const previews = await fetchTellusArticlePreviews(
        agent,
        NEWS_CATEGORIES,
        topCategoryId,
      )
      setArticlesByCategory(groupArticlesByCategory(previews, topCategoryId))
    } catch {
      setArticlesByCategory({})
    } finally {
      setIsRefreshing(false)
      setIsLoading(false)
    }
  }, [agent, topCategoryId])

  React.useEffect(() => {
    setIsLoading(true)
    refreshAll()
  }, [refreshAll])

  const columns = gtMobile ? 2 : 1
  const columnWidth = 1 / columns
  const activeArticles = articlesByCategory[activeCategoryId] ?? []
  const topStory = activeArticles[0]
  const gridStories = activeArticles.slice(1)
  const categoryTitleById = React.useMemo(
    () =>
      new Map(NEWS_CATEGORIES.map(category => [category.id, category.title])),
    [],
  )
  const activeCategoryTitle =
    categoryTitleById.get(activeCategoryId) ?? 'Top Stories'
  const rows = []
  for (let i = 0; i < gridStories.length; i += columns) {
    rows.push(gridStories.slice(i, i + columns))
  }
  const horizontalPadding = gtTablet ? 12 : gtMobile ? 10 : 8
  const topics = ['IA', 'Sécurité', 'Gaming', 'Space', 'Business', 'Pop Culture']

  return (
    <Layout.Screen testID="newsScreen">
      <Layout.Content
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={refreshAll} />
        }
        centerStyle={{
          maxWidth: 980,
          width: '100%',
          alignSelf: 'flex-start',
          marginLeft: 0,
          marginRight: 0,
        }}
        contentContainerStyle={[a.pb_xl, {paddingHorizontal: horizontalPadding}]}>
        <View style={[a.pt_sm, a.gap_sm]}>
          <View style={[a.flex_row, a.align_center, {height: 44}]}>
            <Layout.Header.MenuButton />
            <Layout.Header.Content align="left">
              <Layout.Header.TitleText>
                <Trans>Actualités</Trans>
              </Layout.Header.TitleText>
            </Layout.Header.Content>
            <Layout.Header.Slot />
          </View>
          <View style={[{marginTop: 10, marginBottom: 14}]}>
            <View
              style={[
                a.w_full,
                {
                  height: 1,
                  backgroundColor: t.atoms.border_contrast_low.borderColor,
                  opacity: 0.35,
                },
              ]}
            />
          </View>

          <View
            style={[
              a.flex_row,
              a.align_center,
              a.rounded_full,
              a.border,
              t.atoms.bg_contrast_25,
              t.atoms.border_contrast_low,
              {
                height: 36,
                paddingHorizontal: 6,
                paddingVertical: 3,
                borderRadius: 10,
                alignSelf: 'flex-start',
                marginBottom: 12,
              },
              web({
                backdropFilter: 'blur(14px)',
                backgroundColor: select(t.name, {
                  light: 'rgba(255,255,255,0.55)',
                  dim: 'rgba(20,20,20,0.45)',
                  dark: 'rgba(20,20,20,0.45)',
                }),
                borderColor: t.atoms.border_contrast_low.borderColor,
                boxShadow: '0 6px 18px rgba(0,0,0,0.06)',
              }),
            ]}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={[a.flex_row, {gap: 8}]}>
              {NEWS_CATEGORIES.map(category => {
                const isActive = category.id === activeCategoryId
                return (
                  <Pressable
                    key={category.id}
                    onPress={() => setActiveCategoryId(category.id)}
                    style={({hovered, pressed}) => [
                      a.align_center,
                      a.justify_center,
                      {
                        height: 30,
                        paddingHorizontal: 12,
                        borderRadius: 8,
                        backgroundColor: isActive
                          ? select(t.name, {
                              light: 'rgba(255,255,255,0.85)',
                              dim: 'rgba(255,255,255,0.12)',
                              dark: 'rgba(255,255,255,0.12)',
                            })
                          : select(t.name, {
                              light: 'rgba(255,255,255,0.35)',
                              dim: 'rgba(255,255,255,0.08)',
                              dark: 'rgba(255,255,255,0.08)',
                            }),
                        opacity: hovered ? 0.95 : 1,
                        transform: [
                          {translateY: hovered ? -1 : 0},
                          {scale: pressed ? 0.98 : 1},
                        ],
                      },
                      isActive &&
                        web({
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }),
                    ]}>
                    <Text
                      style={[
                        a.text_sm,
                        a.font_semibold,
                        isActive
                          ? t.atoms.text_contrast_high
                          : t.atoms.text_contrast_medium,
                      ]}
                      numberOfLines={1}>
                      {category.title}
                    </Text>
                  </Pressable>
                )
              })}

              {topics.map(topic => {
                const isActive = topic === activeTopic
                return (
                  <Pressable
                    key={topic}
                    onPress={() => setActiveTopic(topic)}
                    style={({hovered, pressed}) => [
                      a.align_center,
                      a.justify_center,
                      {
                        height: 30,
                        paddingHorizontal: 12,
                        borderRadius: 8,
                        opacity: hovered ? 0.95 : 1,
                        transform: [
                          {translateY: hovered ? -1 : 0},
                          {scale: pressed ? 0.98 : 1},
                        ],
                        backgroundColor: isActive
                          ? select(t.name, {
                              light: 'rgba(255,255,255,0.85)',
                              dim: 'rgba(255,255,255,0.12)',
                              dark: 'rgba(255,255,255,0.12)',
                            })
                          : select(t.name, {
                              light: 'rgba(255,255,255,0.35)',
                              dim: 'rgba(255,255,255,0.08)',
                              dark: 'rgba(255,255,255,0.08)',
                            }),
                      },
                      isActive &&
                        web({
                          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        }),
                    ]}>
                    <Text
                      style={[
                        a.text_sm,
                        a.font_semibold,
                        isActive
                          ? t.atoms.text_contrast_high
                          : t.atoms.text_contrast_medium,
                      ]}
                      numberOfLines={1}>
                      {topic}
                    </Text>
                  </Pressable>
                )
              })}
            </ScrollView>
          </View>

          {isLoading ? (
            <View style={[a.py_xl, a.align_center]}>
              <Loader />
            </View>
          ) : activeArticles.length === 0 ? (
            <View style={[a.px_sm, a.py_xl, a.align_center]}>
              <Text style={[a.text_md, a.font_bold]}>
                {activeCategoryTitle}
              </Text>
              <Text
                style={[
                  a.text_sm,
                  a.pt_sm,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>
                  Post a thread from {displayAtproto(NEWS_AUTHOR_HANDLE)} with{' '}
                  {NEWS_REQUIRED_TAG} to publish here.
                </Trans>
              </Text>
            </View>
          ) : (
            <View style={[a.gap_md, {marginTop: 6}]}>
              <Text style={[a.text_lg, a.font_heavy, {marginBottom: 12}]}>
                {activeCategoryTitle}
              </Text>
              {topStory ? (
                <NewsCard
                  article={topStory}
                  categoryTitle={categoryTitleById.get(activeCategoryId)}
                  variant="hero"
                  to={{
                    screen: 'NewsArticle',
                    params: {
                      uri: topStory.uri,
                    },
                  }}
                />
              ) : null}
              {gridStories.length > 0 ? (
                <View style={[a.gap_md]}>
                  {rows.map((row, rowIndex) => (
                    <Grid.Row key={`top-row-${rowIndex}`} gap={a.gap_md.gap}>
                      {row.map(article => (
                        <Grid.Col
                          key={article.uri}
                          width={row.length === 1 ? 1 : columnWidth}>
                          <NewsCard
                            article={article}
                            categoryTitle={
                              article.categoryId
                                ? categoryTitleById.get(article.categoryId)
                                : categoryTitleById.get(activeCategoryId)
                            }
                            to={{
                              screen: 'NewsArticle',
                              params: {
                                uri: article.uri,
                              },
                            }}
                          />
                        </Grid.Col>
                      ))}
                    </Grid.Row>
                  ))}
                </View>
              ) : null}
            </View>
          )}
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}
